import {useQuery} from "@tanstack/react-query";
import {CustomOutlet} from "components/Admin/CustomOutlet/CustomOutlet";
import {ActionFormPopup} from "components/Admin/Pages/ActionFormPopup/ActionFormPopup";
import {useQuerySearch} from "hooks/useQuerySearch";
import {ReactNode} from "react";
import {useNotify, useResourceContext, useTranslate} from "react-admin";
import {useId} from "../../../../hooks/useId";

type EditPopupProps<TDefaultValue, TProps, TResult> = {
  queryKey?: string;
  label?: string;
  children?: ReactNode;
  updateQuery: (props: { id: any; data: TProps }) => Promise<TResult>;
  onSuccess?: (data: TResult) => void;
  getRecordQuery: (props: { id: any }) => Promise<TResult>;
  renderButtons?: (loading: boolean) => ReactNode;
  convertDefaultValue?: (data: TResult) => TDefaultValue;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
};

export const EditPopup: <TDefaultValue, TProps, TResult extends { id: any }>(
  props: EditPopupProps<TDefaultValue, TProps, TResult>
) => JSX.Element = <TDefaultValue, TProps, TResult extends { id: any }>(
  props: EditPopupProps<TDefaultValue, TProps, TResult>
) => {
  const t = useTranslate();

  const id = useId()

  const notify = useNotify();

  const queryKey = useResourceContext({resource: props.queryKey});

  if (!queryKey) {
    throw new Error("Query key or resource key is required");
  }

  const {foundedEntity} = useQuerySearch<TResult>({
    queryKey: queryKey,
    entityId: id,
  });

  const query = useQuery({
    queryKey: [props.queryKey, id],
    queryFn: () => {
      return props.getRecordQuery({id: id}).catch((error) => {
        if (!error?.message) {
          return;
        }

        notify(error?.message, {type: "error"});
      });
    },
    initialData: foundedEntity,
    // refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
  });

  return (
    <>
      <CustomOutlet/>
      <ActionFormPopup
        queryKey={props.queryKey}
        submitQuery={({data}: { data: TProps }) =>
          props.updateQuery({id: id, data: data})
        }
        submitButtonText={t("ra.action.save")}
        successMessage={t("ra.notification.updated", {smart_count: 1})}
        label={props.label ?? t("ra.action.edit")}
        defaultValue={query.data}
        isLoading={query.isPending}
        zIndex={200}
        renderButtons={props.renderButtons}
        convertDefaultValue={props.convertDefaultValue}
        onSuccess={props.onSuccess}
        maxWidth={props.maxWidth}
      >
        {props.children}
      </ActionFormPopup>
    </>
  );
};